import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  ThreeDRotation as ThreeDIcon,
  WhatsApp as WhatsAppIcon,
  Label as LabelIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as CircleOutlineIcon,
  Lock as LockIcon,
  InfoOutlined as InfoIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { api, ShareLink, ShareLinkEligibleProject, ShareLinkFeature, ShareLinkType } from '../../services/api';
import { ClientTag, createTag, fetchTags, TAG_COLORS } from '../../../super-connector/services/api';
import {
  copyText,
  downloadShareLinkQr,
  SHARE_LINK_TYPE_LABELS,
  shareLinkUrl,
  whatsAppShareUrl,
} from './shareLinkFormat';
import { searchProjects } from '../../utils/projectSearch';
import { isInWebView, saveBlobDownload } from '../../../shared/utils/saveBlobDownload';
import { revealOnFocus, useKeyboardSafeViewport } from '../../../shared/components/sendToClients/keyboardSafeViewport';
import { renderMediaShareCard } from './shareLinkMediaCard';
import { loadEligibleProjects } from './eligibleProjectsCache';
import { SHARE_LINK_FEATURES } from './shareLinkFeatures';
import { AgentCardPreview } from './AgentCardPreview';
import { useAppWindow } from './useAppWindow';
import { FEATURE_ICONS } from './shareLinkFeatureIcons';
import type { PublicAgent } from '../../../share-public/sharePublicApi';
import {
  logSharableLinkAction,
  logSharableLinkCreateAbandoned,
  logSharableLinkCreated,
  logSharableLinkCreateFailed,
} from '../../services/analytics';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (link: ShareLink) => void;
  /** Open straight on the "Your link is ready" step for a link that already exists. */
  existingLink?: ShareLink | null;
  /** False until the agent has shared a Novel Lead Generation distinctive; the 3D Map is always open. */
  featuresUnlocked?: boolean;
  /** Tapped Unlock while `featuresUnlocked` is false — the parent prompts the unlock design. */
  onLockedFeature?: (feature: ShareLinkFeature) => void;
  /**
   * View configuration of an existing link: the same three steps with the project and
   * blocks locked; the description, tag and Customize Profile stay editable (a revoked
   * link opens read-only).
   */
  editLink?: ShareLink | null;
  onSaved?: (link: ShareLink) => void;
  /** Start a new link on this project (e.g. opened from a project page); the agent can still change it. */
  initialProject?: { id: string; name: string } | null;
}

type Step = 'project' | 'customize' | 'profile' | 'done';

/** The numbered steps before the link exists; `done` is the result screen. */
/** `short` names the step in the phone stepper, where the full label would run under the close button. */
const WIZARD_STEPS: Array<{ key: Exclude<Step, 'done'>; label: string; short?: string }> = [
  { key: 'project', label: 'Project' },
  { key: 'customize', label: 'Content' },
  { key: 'profile', label: 'Customize Profile', short: 'Profile' },
];

/** QR view on the done step: the plain QR, or the branded square card for TikTok Live etc. */
type QrStyle = 'simple' | 'media';

/** The 3D map first (always on), then the optional blocks, Super Ratio last. */
const BLOCKS: Array<{ key: 'MAP_3D' | ShareLinkFeature; label: string; blurb: string; icon: React.ReactNode; enabled: boolean; locked?: boolean }> = [
  { key: 'MAP_3D', label: '3D Map', blurb: 'Interactive 3D site plan with live sunlight, stacks and unit types.', icon: <ThreeDIcon />, enabled: true, locked: true },
  ...SHARE_LINK_FEATURES.map((f) => ({ key: f.key, label: f.label, blurb: f.blurb, icon: FEATURE_ICONS[f.key], enabled: f.enabled })),
];

/** Display order of the blocks, for the create payload. */
const FEATURE_ORDER: ShareLinkFeature[] = SHARE_LINK_FEATURES.map((f) => f.key);

/** Every live block starts ticked; the agent unticks what they don't want. Coming-soon ones stay off. */
const DEFAULT_FEATURES: ShareLinkFeature[] = SHARE_LINK_FEATURES.filter((f) => f.enabled).map((f) => f.key);

const DESCRIPTION_MAX = 500;
const CUSTOM_TITLE_MAX = 60;
const TAG_NAME_MAX = 100;

/** A tag option, or the "Create …" row offered for a name the agent doesn't have yet. */
type TagOption = ClientTag & { createName?: string };

const tagColor = (tag: { Color?: string | null } | null | undefined) => tag?.Color || '#ed8936';

export function CreateShareLinkDialog({
  open, onClose, onCreated, existingLink = null, featuresUnlocked = true, onLockedFeature, editLink = null, onSaved,
  initialProject = null,
}: Props) {
  const editing = !!editLink;
  const readOnly = !!editLink?.revokedAt;
  const isMobile = useMediaQuery('(max-width:600px)');
  const viewport = useKeyboardSafeViewport(open && isMobile);
  const tagInputRef = useRef<HTMLInputElement | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  // A text field has focus, so the on-screen keyboard is (probably) up.
  const [fieldFocused, setFieldFocused] = useState(false);
  const keyboardOpen = !!viewport && window.innerHeight - viewport.height > 120;
  // The Android WebView's zoom −/+ pill floats over the bottom-right corner and covers the
  // primary action; lift the actions clear of it (the keyboard hides the pill while it's up).
  const clearZoomControls = isMobile && !keyboardOpen && isInWebView();
  const [step, setStep] = useState<Step>('project');
  // Every link is a 3D Map link; the agent picks the extra blocks around it.
  const linkType: ShareLinkType = 'MAP_3D';
  const [features, setFeatures] = useState<Set<ShareLinkFeature>>(() => new Set());
  // Blocks the server left off the new link because they aren't unlocked yet.
  const [droppedFeatures, setDroppedFeatures] = useState<ShareLinkFeature[]>([]);
  const [projects, setProjects] = useState<ShareLinkEligibleProject[] | null>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  // The project name last filled in as the description, so picking another project replaces it
  // but never what the agent typed.
  const autoDescriptionRef = useRef('');
  const [includeSunlight, setIncludeSunlight] = useState(false);
  const [showDesignation, setShowDesignation] = useState(true);
  // With the title hidden, the agent may put their own tagline in its place.
  const [useCustomTitle, setUseCustomTitle] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  // The agent card visitors will see, for the Customize Profile step's live preview.
  const [agentCard, setAgentCard] = useState<PublicAgent | null>(null);
  const [agentCardError, setAgentCardError] = useState<string | null>(null);
  const [tags, setTags] = useState<ClientTag[] | null>(null);
  const [tag, setTag] = useState<ClientTag | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<ShareLink | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const appWindow = useAppWindow();
  const [qrStyle, setQrStyle] = useState<QrStyle>('simple');
  const [mediaCard, setMediaCard] = useState<{ blob: Blob; url: string } | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('project');
    setInfoOpen(false);
    // Every live block starts ticked, unlocked or not; locked ones are left off when the link is made.
    setFeatures(new Set(DEFAULT_FEATURES));
    setDroppedFeatures([]);
    setQuery(initialProject?.name ?? '');
    setProjectId(initialProject?.id ?? null);
    autoDescriptionRef.current = initialProject?.name ?? '';
    setDescription(autoDescriptionRef.current);
    setIncludeSunlight(false);
    setShowDesignation(true);
    setUseCustomTitle(false);
    setCustomTitle('');
    setTags(null);
    setTag(null);
    setTagInput('');
    setSubmitError(null);
    setCreated(existingLink);
    setQrDataUrl(null);
    setQrStyle('simple');
    setMediaError(null);
    if (existingLink) setStep('done');
    if (editLink) {
      setProjectId(editLink.projectId);
      setDescription(editLink.description || '');
      setIncludeSunlight(!!editLink.includeSunlight);
      setFeatures(new Set(editLink.features ?? []));
      setShowDesignation(editLink.showDesignation !== false);
      setUseCustomTitle(!!editLink.customTitle);
      setCustomTitle(editLink.customTitle || '');
      setTag(editLink.connectorTag
        ? { Id: editLink.connectorTag.id, Name: editLink.connectorTag.name, Color: editLink.connectorTag.color } as ClientTag
        : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seeded on open only
  }, [open, existingLink, editLink]);

  useEffect(() => {
    // A link's project is fixed, so its configuration view never needs the list.
    if (!open || editLink || step !== 'project' || projects) return;
    let cancelled = false;
    setProjectsError(null);
    loadEligibleProjects()
      .then((list) => { if (!cancelled) setProjects(list); })
      .catch((err: Error) => { if (!cancelled) setProjectsError(err.message); });
    return () => { cancelled = true; };
  }, [open, editLink, step, linkType, projects]);

  useEffect(() => {
    if (!open || step !== 'project' || tags) return;
    let cancelled = false;
    // A tag list that fails to load just leaves the picker empty; the tag is optional.
    // Optional. A Picker-only session has no Connector refresh token, and the
    // default signer would treat that 401 as a logout and leave Super Picker.
    fetchTags({ redirectOnAuthError: false })
      .then((list) => { if (!cancelled) setTags(list); })
      .catch(() => { if (!cancelled) setTags([]); });
    return () => { cancelled = true; };
  }, [open, step, tags]);

  // Fetched while the agent picks a project, so the Customize Profile preview is ready on arrival.
  useEffect(() => {
    if (!open || existingLink || agentCard) return;
    let cancelled = false;
    setAgentCardError(null);
    api.getShareLinkAgentCard()
      .then((res) => { if (!cancelled) setAgentCard(res.agent); })
      .catch(() => { if (!cancelled) setAgentCardError('Preview unavailable — your choice still applies.'); });
    return () => { cancelled = true; };
  }, [open, existingLink, agentCard]);

  useEffect(() => {
    if (!created) return;
    QRCode.toDataURL(shareLinkUrl(created), { width: 360, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [created]);

  // Media Share card: rendered on first switch to the tab, once per link.
  useEffect(() => {
    if (!created || qrStyle !== 'media' || mediaCard) return;
    let cancelled = false;
    setMediaError(null);
    api.getCurrentUser()
      .then((user) => renderMediaShareCard(shareLinkUrl(created), {
        name: user.Name || '',
        ceaNumber: user.CEANumber || '',
        mobile: user.Mobile || '',
      }, created.projectName || ''))
      .then((blob) => { if (!cancelled) setMediaCard({ blob, url: URL.createObjectURL(blob) }); })
      .catch((err: Error) => { if (!cancelled) setMediaError(err.message || 'Failed to create the image'); });
    return () => { cancelled = true; };
  }, [created, qrStyle, mediaCard]);

  // Opens the buyer site already unlocked — no WhatsApp code, no lead, no open counted.
  const handlePreview = async (link: ShareLink) => {
    logSharableLinkAction({ action: 'preview', link, source: 'done_dialog' });
    const win = appWindow.open(`Preview · ${link.projectName || 'Share link'}`, `share-preview-${link.id}`);
    try {
      const { url } = await api.getShareLinkPreviewUrl(link.id);
      win.navigate(url);
    } catch (err) {
      win.close();
      setToast(err instanceof Error ? err.message : 'Failed to open preview');
    }
  };

  // A different link needs a fresh card; release the old preview URL.
  useEffect(() => () => { if (mediaCard) URL.revokeObjectURL(mediaCard.url); }, [mediaCard]);
  useEffect(() => { setMediaCard(null); }, [created]);

  const saveMediaCard = async () => {
    if (!mediaCard || !created) return;
    logSharableLinkAction({ action: 'media_card_download', link: created, source: 'done_dialog' });
    const slug = (created.projectName || 'project').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    await saveBlobDownload(mediaCard.blob, `${slug}-3d-sitemap-qr-card-${new Date().toISOString().slice(0, 10)}.png`, 'image/png');
  };

  const filteredProjects = useMemo(() => (projects ? searchProjects(projects, query) : []), [projects, query]);

  const selectedProject = projects?.find((p) => p.projectId === projectId) ?? null;
  // The preset project isn't shareable yet (e.g. north not calibrated) — say so instead of "no match".
  const initialProjectIneligible = !!initialProject && !!projects && projectId === initialProject.id && !selectedProject;

  const tagOptions = useMemo<TagOption[]>(() => {
    const list: TagOption[] = tags ?? [];
    const q = tagInput.trim().toLowerCase();
    if (!q) return list;
    const matches = list.filter((t) => t.Name.toLowerCase().includes(q));
    const exact = list.some((t) => t.Name.toLowerCase() === q);
    const name = tagInput.trim().slice(0, TAG_NAME_MAX);
    return exact ? matches : [...matches, { Id: '__create__', Name: name, createName: name }];
  }, [tags, tagInput]);

  const handleTagChange = async (option: TagOption | null) => {
    if (!option?.createName) {
      setTag(option);
      return;
    }
    setCreatingTag(true);
    setSubmitError(null);
    try {
      const made = await createTag(
        { Name: option.createName, Color: TAG_COLORS[(tags?.length ?? 0) % TAG_COLORS.length] },
        { redirectOnAuthError: false },
      );
      setTags((prev) => [...(prev ?? []), made].sort((a, b) => a.Name.localeCompare(b.Name)));
      setTag(made);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleCreate = async () => {
    if (!projectId || !description.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const withSunlight = !!selectedProject?.sunlightOption && includeSunlight;
      const { link, droppedFeatures: dropped } = await api.createShareLink({
        linkType,
        projectId,
        description: description.trim(),
        includeSunlight: withSunlight,
        connectorTagId: tag?.Id ?? null,
        features: FEATURE_ORDER.filter((f) => features.has(f)),
        showDesignation,
        customTitle: tagline,
      });
      setDroppedFeatures(dropped ?? []);
      setCreated(link);
      setStep('done');
      onCreated?.(link);
      logSharableLinkCreated({
        link,
        includeSunlight: withSunlight,
        hasConnectorTag: !!tag,
        descriptionLength: description.trim().length,
        showDesignation,
        hasCustomTitle: !!tagline,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create link';
      logSharableLinkCreateFailed({ linkType, projectId, error: message });
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const tagline = !showDesignation && useCustomTitle ? customTitle.trim() || null : null;
  const dirty = !!editLink && (
    description.trim() !== (editLink.description || '').trim()
    || (tag?.Id ?? null) !== (editLink.connectorTag?.id ?? null)
    || showDesignation !== (editLink.showDesignation !== false)
    || tagline !== (editLink.customTitle || null)
  );

  const handleSave = async () => {
    if (!editLink || !description.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { link } = await api.updateShareLink(editLink.id, {
        description: description.trim(),
        connectorTagId: tag?.Id ?? null,
        showDesignation,
        customTitle: tagline,
      });
      logSharableLinkAction({ action: 'config_save', link, source: 'config_dialog' });
      onSaved?.(link);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };

  // Closing before the link exists is a drop-off; the QR view or configuration of an existing link never is.
  const handleClose = () => {
    if (!existingLink && !editLink && step !== 'done') logSharableLinkCreateAbandoned({ step, hasProject: !!projectId });
    onClose();
  };

  const canContinue = !!projectId && !!description.trim() && !creatingTag;
  const hasDesignation = !!agentCard?.designation;
  // Ticked "use a tagline" but left it blank: nothing to show, so ask for it before generating.
  const taglineMissing = !showDesignation && useCustomTitle && !customTitle.trim();
  const previewSubtitle = showDesignation
    ? agentCard?.designation ?? null
    : useCustomTitle ? customTitle.trim() || null : null;

  const projectName = editLink ? editLink.projectName || editLink.projectId : selectedProject?.name ?? null;

  const featureLabels = (keys: ShareLinkFeature[]) => {
    const labels = SHARE_LINK_FEATURES.filter((f) => keys.includes(f.key)).map((f) => f.label);
    return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0] ?? '';
  };
  // Ticked blocks the agent hasn't unlocked: the link can still be made, just without them.
  const lockedPicks = !editing && !featuresUnlocked ? FEATURE_ORDER.filter((f) => features.has(f)) : [];

  const selectProject = (p: ShareLinkEligibleProject) => {
    setProjectId(p.projectId);
    setIncludeSunlight(false);
    if (!description.trim() || description === autoDescriptionRef.current) {
      autoDescriptionRef.current = p.name;
      setDescription(p.name);
    }
  };

  const title = step === 'project' ? (editing ? 'Link configuration' : 'Choose a project')
    : step === 'customize' ? (editing ? 'What this link shares' : 'What would you like to share?')
    : step === 'profile' ? 'Customize Profile'
    : 'Your link is ready';

  const saveButton = !readOnly && (
    <Button variant="contained" onClick={handleSave}
      disabled={!dirty || !description.trim() || taglineMissing || creatingTag || submitting}
      startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
      sx={{ bgcolor: 'var(--color-accent)', flexShrink: 0 }}>
      Save changes
    </Button>
  );

  const projectActions = editing ? (
    <>
      <Button onClick={() => setStep('customize')} sx={{ flexShrink: 0, mr: 'auto' }}>Next: Content</Button>
      {saveButton}
    </>
  ) : (
    <>
      {!canContinue && (
        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mr: 'auto', pr: 1 }}>
          {!projectId ? 'Select a project to continue' : 'Add a description to continue'}
        </Typography>
      )}
      <Button variant="contained" onClick={() => setStep('customize')} disabled={!canContinue}
        sx={{ bgcolor: 'var(--color-accent)', flexShrink: 0 }}>
        Next: Content
      </Button>
    </>
  );

  const customizeActions = (
    <>
      <Button onClick={() => setStep('project')}>Back</Button>
      <Button variant="contained" onClick={() => setStep('profile')} disabled={!editing && !canContinue} sx={{ bgcolor: 'var(--color-accent)' }}>
        Next: Customize Profile
      </Button>
    </>
  );

  const profileActions = editing ? (
    <>
      <Button onClick={() => setStep('customize')} disabled={submitting} sx={{ mr: 'auto' }}>Back</Button>
      {readOnly ? <Button variant="contained" onClick={onClose} sx={{ bgcolor: 'var(--color-accent)' }}>Close</Button> : saveButton}
    </>
  ) : (
    <>
      <Button onClick={() => setStep('customize')} disabled={submitting}>Back</Button>
      <Button
        variant="contained"
        onClick={handleCreate}
        disabled={!canContinue || taglineMissing || submitting}
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ bgcolor: 'var(--color-accent)' }}
      >
        Generate link
      </Button>
    </>
  );

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.key === step);

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullScreen={isMobile} fullWidth maxWidth="sm"
      // The block picker gets a little more width so each two-up card's title stays on one line.
      PaperProps={{ sx: {
        bgcolor: 'var(--color-surface)', backgroundImage: 'none',
        ...((step === 'customize' || step === 'profile') && !isMobile && { maxWidth: 720 }),
        // Mobile: end the sheet at the keyboard's top edge so the actions stay reachable.
        ...(viewport && { height: `${viewport.height}px`, maxHeight: `${viewport.height}px`, mt: `${viewport.top}px`, alignSelf: 'flex-start' }),
      } }}>
      <DialogTitle sx={{ pr: 6, color: 'var(--color-text-strong)', fontWeight: 700 }}>
        {stepIndex >= 0 && (
          <Box component="ol" aria-label="Steps" sx={{ display: 'flex', alignItems: 'center', listStyle: 'none', p: 0, m: 0, mb: 1.5, mr: 1.5 }}>
            {WIZARD_STEPS.map((s, i) => {
              const active = i === stepIndex;
              const complete = i < stepIndex;
              // Back is always open; forward only once the project step is filled in.
              const reachable = !active && !submitting && (editing || complete || canContinue);
              return (
                <Box component="li" key={s.key} sx={{ display: 'flex', alignItems: 'center', flex: i > 0 ? 1 : 'none', minWidth: 0 }}>
                  {i > 0 && (
                    <Box aria-hidden sx={{ flex: 1, minWidth: 20, height: 2, mx: 1, borderRadius: 1,
                      bgcolor: i <= stepIndex ? 'var(--color-accent)' : 'rgb(var(--color-text-rgb) / 0.14)', transition: 'background-color 0.2s' }} />
                  )}
                  <Box
                    component="button"
                    type="button"
                    aria-current={active ? 'step' : undefined}
                    aria-label={`Step ${i + 1} of ${WIZARD_STEPS.length}: ${s.label}${complete ? ' (done)' : ''}`}
                    disabled={!reachable}
                    onClick={() => setStep(s.key)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.75, p: 0.5, m: -0.5, border: 0, borderRadius: 999,
                      bgcolor: 'transparent', font: 'inherit', cursor: reachable ? 'pointer' : 'default',
                      color: active || complete ? 'var(--color-text-strong)' : 'var(--color-text-muted)',
                      '&:hover:not(:disabled) .step-dot': { boxShadow: '0 0 0 3px rgb(var(--color-accent-rgb) / 0.25)' },
                    }}
                  >
                    <Box className="step-dot" sx={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                      fontSize: 12, fontWeight: 800, transition: 'background-color 0.2s, box-shadow 0.2s',
                      ...(active || complete
                        ? { bgcolor: 'var(--color-accent)', color: '#fff' }
                        : { border: '1.5px solid rgb(var(--color-text-rgb) / 0.3)' }),
                    }}>
                      {complete ? <CheckIcon sx={{ fontSize: 15 }} /> : i + 1}
                    </Box>
                    {/* Phones: only the current step is named, so three steps fit beside the close button. */}
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: active ? 700 : 600, whiteSpace: 'nowrap', display: { xs: active ? 'inline' : 'none', sm: 'inline' } }}>{isMobile ? s.short ?? s.label : s.label}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        {step === 'project' && !editing ? (
          // The project step needs the room for its fields; its instructions live behind the (i).
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {title}
            <ClickAwayListener onClickAway={() => setInfoOpen(false)}>
              <Tooltip
                open={infoOpen}
                title="Tick the project to share, then describe the link. Only Top Viewing projects with north calibrated are listed."
                placement="bottom-start"
                disableFocusListener
                disableHoverListener
                disableTouchListener
                arrow
              >
                <IconButton size="small" aria-label="How this step works" onClick={() => setInfoOpen((v) => !v)}
                  sx={{ color: 'var(--color-text-muted)' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ClickAwayListener>
          </Box>
        ) : (
          <>
            {title}
            <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-muted)', fontWeight: 400, mt: 0.5 }}>
              {editing && readOnly && 'This link is revoked, so its configuration is view-only.'}
              {editing && !readOnly && step === 'project' && 'The project is fixed. You can edit the description and tag.'}
              {editing && !readOnly && step === 'customize' && 'Blocks are fixed once a link is made. Create a new link to share a different set.'}
              {editing && !readOnly && step === 'profile' && 'Changes apply to the live link as soon as you save.'}
              {!editing && step === 'customize' && <>
                {projectName && <>Sharing <strong>{projectName}</strong>. </>}
                The 3D Map is always included. Tick the extra blocks your visitors can tap on.
              </>}
              {!editing && step === 'profile' && 'Choose how your agent card looks to visitors on this link.'}
              {step === 'done' && 'Share it anywhere — every verified visitor becomes a lead.'}
            </Typography>
          </>
        )}
        <IconButton aria-label="Close" onClick={handleClose} disabled={submitting} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          borderColor: 'rgba(var(--color-text-rgb), 0.1)',
          // Project step on desktop: only the project list scrolls; search and description stay pinned.
          // On mobile the whole step scrolls, so the fields below the list never sit behind the keyboard.
          ...(step === 'project' && !isMobile && { display: 'flex', flexDirection: 'column', overflow: 'hidden' }),
          // Some Android WebViews draw the keyboard over the page without resizing it, so nothing
          // reports its height. While a field has focus, leave room to scroll that field up to the
          // top, clear of the keyboard; with the dialog already resized it isn't needed.
          ...(isMobile && fieldFocused && !keyboardOpen && { pb: '60vh' }),
          '& .MuiTextField-root': { scrollMarginTop: 8 },
        }}
        onFocus={(e) => { if (e.target.matches('input, textarea')) setFieldFocused(true); }}
        onBlur={(e) => { if (e.target.matches('input, textarea')) setFieldFocused(false); }}
      >
        {step === 'customize' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 1 }}>
            <Box>
              <Box role="group" aria-label="Blocks on this link" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {BLOCKS.map((block) => {
                  const selected = block.locked || (block.key !== 'MAP_3D' && features.has(block.key));
                  // An extra block the agent hasn't unlocked yet (the 3D Map is always open). It stays
                  // ticked so it's on once unlocked; tapping it prompts the unlock instead.
                  const gated = !editing && !featuresUnlocked && block.enabled && !block.locked;
                  const toggle = () => {
                    if (editing || block.locked || !block.enabled || block.key === 'MAP_3D') return;
                    const key = block.key;
                    if (gated) {
                      onLockedFeature?.(key);
                      return;
                    }
                    setFeatures((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    });
                  };
                  return (
                    <Box
                      key={block.key}
                      component="button"
                      type="button"
                      role="checkbox"
                      aria-checked={selected}
                      aria-disabled={editing || block.locked || !block.enabled}
                      disabled={!block.enabled}
                      onClick={toggle}
                      sx={{
                        position: 'relative', textAlign: 'left', p: 2, borderRadius: 2,
                        display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start',
                        cursor: editing || block.locked ? 'default' : block.enabled ? 'pointer' : 'not-allowed',
                        border: '1.5px solid',
                        borderColor: selected ? 'var(--color-accent)' : 'rgb(var(--color-text-rgb) / 0.14)',
                        bgcolor: selected ? 'rgb(var(--color-accent-rgb) / 0.08)' : 'transparent',
                        // Configuration view: the link's own blocks stay bright, the rest fade back.
                        color: 'var(--color-text-strong)', opacity: !block.enabled ? 0.5 : editing && !selected ? 0.55 : 1, font: 'inherit',
                        transition: 'border-color 0.15s, background-color 0.15s',
                        '&:hover:not(:disabled)': editing || block.locked ? {} : { borderColor: 'var(--color-accent)' },
                      }}
                    >
                      {/* Title row: icon, name, then the tick or status on the same line. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, minWidth: 0, minHeight: 24, color: block.enabled ? 'var(--color-accent)' : 'inherit' }}>
                        {block.icon}
                        <Typography title={block.label} sx={{ flex: 1, fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.25, minWidth: 0 }}>{block.label}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 0.5 }}>
                          {!block.enabled ? (
                            <Typography component="span" sx={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                              Coming soon
                            </Typography>
                          ) : block.locked ? (
                            <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-accent)', whiteSpace: 'nowrap' }}>
                              <LockIcon sx={{ fontSize: 13 }} /> Always on
                            </Typography>
                          ) : gated ? (
                            <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-accent)', whiteSpace: 'nowrap' }}>
                              <LockIcon sx={{ fontSize: 13 }} /> Unlock
                            </Typography>
                          ) : selected ? (
                            <CheckCircleIcon fontSize="small" sx={{ color: 'var(--color-accent)' }} />
                          ) : (
                            <CircleOutlineIcon fontSize="small" sx={{ color: 'rgb(var(--color-text-rgb) / 0.4)' }} />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>{block.blurb}</Typography>
                    </Box>
                  );
                })}
              </Box>
              {lockedPicks.length > 0 && (
                <Alert
                  severity="info"
                  sx={{ mt: 1.5 }}
                  action={onLockedFeature && (
                    <Button color="inherit" size="small" onClick={() => onLockedFeature(lockedPicks[0])}>Unlock</Button>
                  )}
                >
                  {featureLabels(lockedPicks)} {lockedPicks.length > 1 ? "aren't" : "isn't"} activated for you yet, so {lockedPicks.length > 1 ? 'they' : 'it'} won't be turned on and won't be available on this link. You can still create the link; share a Novel Lead Generation design to unlock {lockedPicks.length > 1 ? 'them' : 'it'}.
                </Alert>
              )}
            </Box>
          </Box>
        )}

        {step === 'profile' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 1 }}>
            <Box component="section" aria-labelledby="share-link-designation-heading">
              <Typography id="share-link-designation-heading" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                Show your title?
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.25, mb: 1.5 }}>
                Visitors see your agent card on the link. Tap an option to see how it looks.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1.15fr)' }, gap: 2, alignItems: 'center' }}>
                <Box role="radiogroup" aria-labelledby="share-link-designation-heading"
                  sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                  {[
                    { value: true, label: 'Show title', hint: 'From your profile, under your name', icon: <VisibilityIcon fontSize="small" /> },
                    { value: false, label: 'Hide title', hint: useCustomTitle ? 'Your tagline shows instead' : 'Name, CEA No and agency only', icon: <VisibilityOffIcon fontSize="small" /> },
                  ].map((opt) => {
                    const selected = showDesignation === opt.value;
                    return (
                      <Box
                        key={opt.label}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={readOnly}
                        onClick={() => setShowDesignation(opt.value)}
                        sx={{
                          display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 2, textAlign: 'left', cursor: readOnly ? 'default' : 'pointer',
                          font: 'inherit', minWidth: 0, border: '1.5px solid',
                          borderColor: selected ? 'var(--color-accent)' : 'rgb(var(--color-text-rgb) / 0.14)',
                          bgcolor: selected ? 'rgb(var(--color-accent-rgb) / 0.08)' : 'transparent',
                          color: 'var(--color-text-strong)', transition: 'border-color 0.15s, background-color 0.15s',
                          '&:hover:not(:disabled)': { borderColor: 'var(--color-accent)' },
                        }}
                      >
                        <Box sx={{ color: selected ? 'var(--color-accent)' : 'var(--color-text-muted)', display: 'flex', pt: '1px' }}>{opt.icon}</Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{opt.label}</Typography>
                          <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-muted)', lineHeight: 1.35, mt: 0.25, overflowWrap: 'anywhere' }}>
                            {opt.hint}
                          </Typography>
                        </Box>
                        {selected
                          ? <CheckCircleIcon fontSize="small" sx={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                          : <CircleOutlineIcon fontSize="small" sx={{ color: 'rgb(var(--color-text-rgb) / 0.4)', flexShrink: 0 }} />}
                      </Box>
                    );
                  })}
                  <Collapse in={!showDesignation} timeout={200} unmountOnExit>
                    <Box sx={{ pl: 1, pt: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={useCustomTitle} disabled={readOnly} onChange={(e) => setUseCustomTitle(e.target.checked)} />}
                        label={<Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>Show a tagline instead</Typography>}
                      />
                      <Collapse in={useCustomTitle} timeout={200}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Your tagline"
                          placeholder="e.g. Your Thomson Reserve specialist"
                          disabled={readOnly}
                          value={customTitle}
                          onChange={(e) => setCustomTitle(e.target.value.slice(0, CUSTOM_TITLE_MAX))}
                          onFocus={isMobile ? revealOnFocus : undefined}
                          inputProps={{ maxLength: CUSTOM_TITLE_MAX, enterKeyHint: 'done' }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                          helperText={taglineMissing
                            ? 'Type a tagline, or untick the box to show nothing.'
                            : `Shows under your name in place of your title. ${customTitle.length}/${CUSTOM_TITLE_MAX}`}
                          sx={{ mt: 0.5 }}
                        />
                      </Collapse>
                    </Box>
                  </Collapse>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, background: 'linear-gradient(160deg, #1d2a3d 0%, #0b1220 100%)' }}>
                  <Typography component="div" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(238, 242, 247, 0.55)', mb: 1, textAlign: 'center' }}>
                    Live preview · what visitors see
                  </Typography>
                  <AgentCardPreview agent={agentCard} error={agentCardError} subtitle={previewSubtitle} />
                </Box>
              </Box>
              {agentCard && !hasDesignation && showDesignation && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Your profile has no title yet, so nothing shows under your name. Hide it to add a tagline instead.
                </Alert>
              )}
            </Box>
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Box>
        )}

        {step === 'project' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
            {editing ? (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 1.5, flexShrink: 0,
                border: '1.5px solid var(--color-accent)', bgcolor: 'rgba(var(--color-accent-rgb), 0.08)',
              }}>
                <ThreeDIcon sx={{ color: 'var(--color-accent)' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'var(--color-text-strong)' }} noWrap>{projectName}</Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-muted)' }} noWrap>
                    {editLink && shareLinkUrl(editLink)}{editLink?.includeSunlight ? ' · with Sun & light' : ''}
                  </Typography>
                </Box>
                <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                  <LockIcon sx={{ fontSize: 13 }} /> Fixed
                </Typography>
              </Box>
            ) : <>
            <TextField
              size="small"
              placeholder="Search project name, district or area"
              inputProps={{ 'aria-label': 'Select a project' }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={isMobile ? revealOnFocus : undefined}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Box sx={{
              flex: isMobile ? '0 0 auto' : '1 1 auto', minHeight: 120, maxHeight: isMobile ? 'min(240px, 40vh)' : 300, overflowY: 'auto',
              WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
              display: 'flex', flexDirection: 'column', gap: 1,
            }}>
              {!projects && !projectsError && (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>
              )}
              {projectsError && <Alert severity="error">{projectsError}</Alert>}
              {projects && filteredProjects.length === 0 && (
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 3, textAlign: 'center' }}>
                  {initialProjectIneligible && query === initialProject?.name
                    ? `${initialProject?.name} isn't ready for 3D sharing yet. North must be calibrated on the site plan first.`
                    : projects.length === 0
                    ? 'No projects are ready for 3D sharing yet. North must be calibrated on the site plan first.'
                    : 'No projects match your search.'}
                </Typography>
              )}
              {filteredProjects.map((p) => {
                const selected = p.projectId === projectId;
                return (
                  <Box
                    key={p.projectId}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => selectProject(p)}
                    sx={{
                      flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1.5, cursor: 'pointer',
                      textAlign: 'left', font: 'inherit', color: 'var(--color-text-strong)', border: '1.5px solid',
                      borderColor: selected ? 'var(--color-accent)' : 'transparent',
                      bgcolor: selected ? 'rgba(var(--color-accent-rgb), 0.08)' : 'rgba(var(--color-text-rgb), 0.04)',
                      '&:hover': { borderColor: 'rgba(var(--color-accent-rgb), 0.6)' },
                    }}
                  >
                    <Box sx={{
                      width: 56, height: 42, borderRadius: 1, flexShrink: 0, bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                      backgroundImage: p.thumbnailUrl ? `url("${p.thumbnailUrl}")` : 'none', backgroundSize: 'cover', backgroundPosition: 'center',
                    }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>{p.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }} noWrap component="div">
                        {[p.district ? `D${p.district}` : null, p.area].filter(Boolean).join(' · ') || p.address}
                      </Typography>
                    </Box>
                    {/* Display-only: the whole row is the click target. */}
                    <Checkbox
                      checked={selected}
                      icon={<CircleOutlineIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      sx={{ p: 0.5, pointerEvents: 'none', color: 'rgba(var(--color-text-rgb), 0.4)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                    />
                  </Box>
                );
              })}
            </Box>
            {selectedProject?.sunlightOption && (
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={includeSunlight} onChange={(e) => setIncludeSunlight(e.target.checked)} />}
                  label="Include sunlight"
                />
                <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-muted)', ml: 4, mt: -0.5 }}>
                  Adds the Sun &amp; light tab. The link is shared on {selectedProject.name}'s own website instead of a short link.
                </Typography>
              </Box>
            )}
            </>}
            <TextField
              size="small"
              label="Description"
              required
              disabled={readOnly}
              placeholder={projectName
                ? `e.g. ${projectName} 3D site experience for warm leads`
                : 'e.g. 3D site experience for warm leads'}
              helperText={`What is this link for? Only you can see this note.${description.length > DESCRIPTION_MAX - 100 ? ` ${description.length}/${DESCRIPTION_MAX}` : ''}`}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
              onFocus={isMobile ? revealOnFocus : undefined}
              // Enter moves on to the tag, so the agent sees the next step instead of a stuck keyboard.
              inputProps={{ enterKeyHint: 'next' }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                tagInputRef.current?.focus();
              }}
              sx={{ flexShrink: 0 }}
            />
            <Autocomplete<TagOption>
              size="small"
              sx={{ flexShrink: 0 }}
              options={tagOptions}
              value={tag}
              inputValue={tagInput}
              onInputChange={(_, v) => setTagInput(v)}
              onChange={(_, v) => { void handleTagChange(v); }}
              filterOptions={(opts) => opts}
              getOptionLabel={(opt) => opt.Name || ''}
              isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
              loading={!tags || creatingTag}
              disabled={creatingTag || readOnly}
              noOptionsText="No tags yet — type a name to create one"
              renderOption={(props, option) => {
                const { key: _key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
                return (
                  <li {...liProps} key={option.Id}>
                    {option.createName ? (
                      <Typography variant="body2">Create tag “<strong>{option.createName}</strong>”</Typography>
                    ) : (
                      <Chip label={option.Name} size="small"
                        sx={{ bgcolor: `${tagColor(option)}22`, color: tagColor(option), fontWeight: 700 }} />
                    )}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // InputBase forks this with Autocomplete's own inputProps.ref.
                  inputRef={tagInputRef}
                  onFocus={isMobile ? revealOnFocus : undefined}
                  label="Super Connector tag (optional)"
                  placeholder="Pick or create a tag"
                  helperText="Every lead from this link gets this tag in Super Connector."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LabelIcon fontSize="small" sx={{ color: tag ? tagColor(tag) : 'var(--color-text-muted)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {creatingTag && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {submitError && <Alert severity="error" sx={{ flexShrink: 0 }}>{submitError}</Alert>}
            {/* Mobile: the actions follow the fields, so the next step scrolls up with them above the keyboard. */}
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, flexShrink: 0, ...(clearZoomControls && { mb: 6 }) }}>
                {projectActions}
              </Box>
            )}
          </Box>
        )}

        {step === 'done' && created && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
            {droppedFeatures.length > 0 && (
              <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
                {featureLabels(droppedFeatures)} {droppedFeatures.length > 1 ? "aren't" : "isn't"} on this link because {droppedFeatures.length > 1 ? "they're" : "it's"} not activated for you yet.
              </Alert>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={qrStyle}
              onChange={(_, value: QrStyle | null) => {
                if (!value) return;
                if (value === 'media' && qrStyle !== 'media') logSharableLinkAction({ action: 'media_card_view', link: created, source: 'done_dialog' });
                setQrStyle(value);
              }}
              aria-label="QR style"
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2, color: 'rgba(var(--color-text-rgb), 0.72)' },
                '& .MuiToggleButton-root.Mui-selected': { color: 'var(--color-accent)', bgcolor: 'rgba(var(--color-accent-rgb), 0.14)' },
              }}
            >
              <ToggleButton value="simple">Simple</ToggleButton>
              <ToggleButton value="media">Media Share</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {step === 'done' && created && qrStyle === 'media' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pb: 1 }}>
            <Box sx={{ width: '100%', maxWidth: 400, aspectRatio: '1 / 1', borderRadius: 2, overflow: 'hidden', bgcolor: '#0B1220',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {mediaCard ? (
                <Box component="img" src={mediaCard.url} alt="QR share card" sx={{ width: '100%', height: '100%', display: 'block' }} />
              ) : mediaError ? (
                <Typography variant="body2" sx={{ color: '#F5F1EA', px: 3, textAlign: 'center' }}>{mediaError}</Typography>
              ) : (
                <CircularProgress size={28} sx={{ color: '#F28C38' }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: 400 }}>
              A square 1080 × 1080 image with your name, CEA No and mobile. Show it on screen during your TikTok Live or post it anywhere. Viewers scan to open this link.
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} disabled={!mediaCard} onClick={() => { void saveMediaCard(); }}
              sx={{ bgcolor: 'var(--color-accent)', fontWeight: 700 }}>
              Save image
            </Button>
          </Box>
        )}

        {step === 'done' && created && qrStyle === 'simple' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pb: 1 }}>
            {qrDataUrl && (
              <Box component="img" src={qrDataUrl} alt="QR code for the link"
                sx={{ width: 180, height: 180, borderRadius: 2, bgcolor: '#fff', p: 1 }} />
            )}
            <Typography sx={{ fontWeight: 700, color: 'var(--color-text-strong)', textAlign: 'center' }}>
              {created.projectName} · {SHARE_LINK_TYPE_LABELS[created.linkType]}
            </Typography>
            {!!created.features?.length && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', mt: -1 }}>
                {created.features.map((key) => (
                  <Chip key={key} size="small" icon={<Box component="span" sx={{ display: 'inline-flex', '& svg': { fontSize: 15 } }}>{FEATURE_ICONS[key]}</Box>}
                    label={SHARE_LINK_FEATURES.find((f) => f.key === key)?.label ?? key}
                    sx={{ bgcolor: 'rgb(var(--color-accent-rgb) / 0.12)', color: 'var(--color-text-strong)', fontWeight: 600, '& .MuiChip-icon': { color: 'var(--color-accent)' } }} />
                ))}
              </Box>
            )}
            {created.showDesignation === false && (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', textAlign: 'center', mt: -1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <VisibilityOffIcon sx={{ fontSize: 16 }} />
                {created.customTitle ? <>“{created.customTitle}” shows in place of your title</> : 'Your title is hidden on this link'}
              </Typography>
            )}
            {created.connectorTag && (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', textAlign: 'center', mt: -1 }}>
                Leads will be tagged <strong style={{ color: created.connectorTag.color || '#ed8936' }}>{created.connectorTag.name}</strong> in Super Connector
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              value={shareLinkUrl(created)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="Copy link" onClick={async () => {
                      logSharableLinkAction({ action: 'copy', link: created, source: 'done_dialog' });
                      setToast(await copyText(shareLinkUrl(created)) ? 'Link copied' : 'Copy failed');
                    }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button variant="contained" startIcon={<WhatsAppIcon />} href={whatsAppShareUrl(created)} target="_blank" rel="noopener"
                onClick={() => logSharableLinkAction({ action: 'whatsapp', link: created, source: 'done_dialog' })}
                sx={{ bgcolor: '#25d366', color: '#05311a', fontWeight: 700, '&:hover': { bgcolor: '#1ebe5b' } }}>
                Share on WhatsApp
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => {
                logSharableLinkAction({ action: 'qr_download', link: created, source: 'done_dialog' });
                void downloadShareLinkQr(created);
              }}>
                Save QR
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!(step === 'project' && isMobile) && (
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, ...(clearZoomControls && { pb: 8 }) }}>
          {step === 'project' && projectActions}
          {step === 'customize' && customizeActions}
          {step === 'profile' && profileActions}
          {step === 'done' && created && (
            <Button startIcon={<OpenInNewIcon />} onClick={() => void handlePreview(created)} sx={{ mr: 'auto' }}>Preview</Button>
          )}
          {step === 'done' && <Button variant="contained" onClick={onClose} sx={{ bgcolor: 'var(--color-accent)' }}>Done</Button>}
        </DialogActions>
      )}

      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      {appWindow.element}
    </Dialog>
  );
}
